import { Money } from '@principle-theorem/accounting';
import {
  Integration,
  IntegrationCapability,
  IntegrationType,
} from '@principle-theorem/integrations';
import {
  AnyAutomation,
  AutomatedNotificationType,
  IAutomation,
  ICampaignEntryReportResult,
  ICampaignRunEstimates,
  IMarketingCampaign,
  IMarketingCampaignStep,
  IRenderedTemplate,
  ISMSIntegrationData,
  ITemplateContext,
  TemplateScope,
  isAutomatedNotification,
  isAutomatedNotificationConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  asReffable,
  asyncForEach,
  getSmsSegmentCount,
  sanitiseSmsBody,
} from '@principle-theorem/shared';
import * as he from 'he';
import { isEmpty, sum } from 'lodash';
import { MOCK_ALL_RESOLVER } from '../../templating/mock-resolvers';
import { compileTemplate } from '../../templating/templating';
import { Automation } from '../automation/automation';
import { Brand } from '../brand';
import { BillingMeter } from '../organisation/billing-meter';
import { Organisation } from '../organisation/organisation';
import { MarketingCampaign } from './campaign';

export interface ICampaignEstimate {
  smsSegments: number;
}

export class CampaignCostEstimate {
  static async calculateEstimates(
    campaign: WithRef<IMarketingCampaign>,
    newEntries: ICampaignEntryReportResult[],
    maxEntries?: number
  ): Promise<ICampaignRunEstimates> {
    // maxEntries of 0 or undefined means "no cap" — all entries are included.
    // Clamp to a non-negative integer to guard against bad input.
    const safeCap =
      maxEntries && maxEntries > 0 ? Math.floor(maxEntries) : undefined;
    const cappedEntries = safeCap ? newEntries.slice(0, safeCap) : newEntries;
    const campaignSteps = await MarketingCampaign.orderedSteps(campaign);
    const entryEstimates = await CampaignCostEstimate.estimateForRun(
      campaignSteps,
      cappedEntries
    );

    const brandRef = MarketingCampaign.brandRef(asReffable(campaign.ref));
    const organisationRef = Brand.organisationRef(asReffable(brandRef));
    const organisation = await Firestore.getDoc(organisationRef);

    const smsIntegrations =
      await Integration.getByCapability<ISMSIntegrationData>(
        Organisation.integrationCol(organisation),
        IntegrationCapability.SMS
      );
    const smsProvider =
      organisation.integrations?.smsProvider ?? IntegrationType.Twilio;
    const integration = smsIntegrations.find((smsIntegration) => smsIntegration.type === smsProvider);

    if (!integration?.data.marketingPriceId) {
      // eslint-disable-next-line no-console
      console.error(
        `No pricing for integration found for organisation: ${organisation.ref.id}`
      );
    }

    const costPerSegment = integration?.data.marketingPriceId
      ? BillingMeter.resolveCostByLookupId(integration.data.marketingPriceId)
      : 0;
    return CampaignCostEstimate.getEstimates(
      newEntries,
      cappedEntries,
      entryEstimates,
      costPerSegment
    );
  }

  static getEstimates(
    allEntries: ICampaignEntryReportResult[],
    entriesForCosting: ICampaignEntryReportResult[],
    estimate: ICampaignEstimate,
    costPerSegmentValue: number
  ): ICampaignRunEstimates {
    const estimatedCost = CampaignCostEstimate.estimateCostAmount(
      estimate,
      costPerSegmentValue
    );
    const entriesThisRunCount = entriesForCosting.length;
    const averageCostPerEntry =
      entriesThisRunCount !== 0
        ? Money.from(estimatedCost).divide(entriesThisRunCount)
        : 0;
    return {
      newEntries: allEntries.length,
      entriesThisRun: entriesThisRunCount,
      costPerEntry: Money.amount(averageCostPerEntry),
      estimatedCost,
    };
  }

  static estimateCostAmount(
    estimate: ICampaignEstimate,
    costPerSegment: number
  ): number {
    return Money.amount(
      Money.from(costPerSegment).multiply(estimate.smsSegments)
    );
  }

  static async estimateForRun(
    campaignSteps: WithRef<IMarketingCampaignStep>[],
    entries: ICampaignEntryReportResult[]
  ): Promise<ICampaignEstimate> {
    const estimates = await asyncForEach(entries, (entry) =>
      this.estimateForEntry(campaignSteps, entry)
    );
    return this.reduceCampaignEstimates(estimates);
  }

  static async estimateForEntry(
    campaignSteps: WithRef<IMarketingCampaignStep>[],
    entry?: ICampaignEntryReportResult
  ): Promise<ICampaignEstimate> {
    const estimates = await asyncForEach(campaignSteps, (step) =>
      this.estimateForStep(step, entry)
    );
    return this.reduceCampaignEstimates(estimates);
  }

  static async estimateForStep(
    campaignStep: WithRef<IMarketingCampaignStep>,
    entry?: ICampaignEntryReportResult
  ): Promise<ICampaignEstimate> {
    const smsSegments = await this.estimateSMSSegmentsForStep(
      campaignStep,
      entry
    );
    return { smsSegments };
  }

  static async estimateSMSSegmentsForStep(
    campaignStep: WithRef<IMarketingCampaignStep>,
    entry?: ICampaignEntryReportResult
  ): Promise<number> {
    if (
      !isAutomatedNotification(campaignStep.config) ||
      campaignStep.config.type !== AutomatedNotificationType.SMS
    ) {
      return 0;
    }

    if (entry && !entry.patientMobile?.trim()) {
      return 0;
    }

    const template = campaignStep.config.customTemplate;
    if (!template) {
      return 0;
    }

    const scope = template.scope ?? TemplateScope.Patient;
    const templateContext = await MOCK_ALL_RESOLVER.resolve([scope]);
    const body = prepareSMSBody(
      template.renderedTemplate?.text ?? '',
      templateContext
    );
    const segments = getSmsSegmentCount(body);

    return segments;
  }

  static reduceCampaignEstimates(
    estimates: ICampaignEstimate[]
  ): ICampaignEstimate {
    const smsSegments = estimates.map((estimate) => estimate.smsSegments);
    return {
      smsSegments: sum(smsSegments),
    };
  }

  static async estimateForAutomation(
    automation: IAutomation<AnyAutomation>
  ): Promise<ICampaignEstimate> {
    if (
      !isAutomatedNotification(automation.data) ||
      automation.data.type !== AutomatedNotificationType.SMS
    ) {
      return { smsSegments: 0 };
    }
    const template =
      await CampaignCostEstimate.resolveAutomationTemplate(automation);
    const context = await approxTemplateContext(
      Automation.determineScope(automation)
    );
    const body = prepareSMSBody(template?.text ?? '', context);
    return { smsSegments: getSmsSegmentCount(body) };
  }

  static async resolveAutomationTemplate(
    automation: IAutomation<AnyAutomation>
  ): Promise<IRenderedTemplate | undefined> {
    if (!isAutomatedNotification(automation.data)) {
      return;
    }
    if (automation.data.customTemplate) {
      return automation.data.customTemplate.renderedTemplate;
    }
    if (!automation.configRef) {
      return;
    }

    const config = await Firestore.getDoc(automation.configRef);
    if (!isAutomatedNotificationConfiguration(config)) {
      return;
    }

    const template = await Firestore.getDoc(config.templateRef);
    return template.renderedTemplate;
  }
}

export function prepareSMSBody(
  templateBody: string,
  templateContext?: ITemplateContext
): string {
  const populatedBody =
    templateContext && !isEmpty(templateContext)
      ? compileTemplate(templateBody, templateContext)
      : templateBody;
  const decodedBody = he.decode(populatedBody);
  return sanitiseSmsBody(decodedBody);
}

// TODO: Complete this
// export async function buildTemplateScopeData(
//   data: IAutomationScopeData,
//   defaultPracticeRef: DocumentReference<IPractice>,
//   appUrl: string
// ): Promise<ITemplateScopeData> {
//   const practice = await OrganisationCache.practices.getDoc(defaultPracticeRef);
//   switch (data.scope) {
//     case TemplateScope.Patient:
//       return ScopeDataBuilder.buildPatientScopeData(
//         asReffable(data.patientRef),
//         asReffable(defaultPracticeRef),
//         appUrl
//       );
//     case TemplateScope.Appointment:
//       const appointment = await Firestore.getDoc(data.appointmentRef);
//       return ScopeDataBuilder.buildAppointmentScopeData(appointment, appUrl);
//     case TemplateScope.Invoice:
//       const invoice = await Firestore.getDoc(data.invoiceRef);
//       return ScopeDataBuilder.buildInvoiceScopeData(invoice, appUrl);
//     case TemplateScope.TreatmentPlan:
//       const treatmentPlan = await Firestore.getDoc(data.treatmentPlanRef);
//       return ScopeDataBuilder.buildTreatmentPlanScopeData(
//         treatmentPlan,
//         practice,
//         appUrl
//       );
//     case TemplateScope.TreatmentStep:
//       const treatmentStep = await Firestore.getDoc(data.treatmentStepRef);
//       return ScopeDataBuilder.buildTreatmentStepScopeData(
//         treatmentStep,
//         practice,
//         appUrl
//       );
//     case TemplateScope.None:
//     default:
//       return {};
//   }
// }

async function approxTemplateContext(
  scope: TemplateScope
): Promise<ITemplateContext> {
  return MOCK_ALL_RESOLVER.resolve([scope ?? TemplateScope.Patient]);
}
